import { ArrowRight, ChartLineUp } from '@phosphor-icons/react';
import { Stack, Text } from '@fragments-sdk/ui';
import type { DashboardDrillTarget } from './dashboard-brief';
import { SparkBg } from './dashboard-helpers';
import type { DashboardFeedItem, DashboardMetrics } from './dashboard-surface';
import s from './dashboard.module.scss';

// ── Activity + drift trend (operational mode) ────────────────────

export function DashboardActivity({
  metrics,
  onNavigate,
}: {
  metrics: DashboardMetrics;
  onNavigate?: (target: DashboardDrillTarget) => void;
}) {
  return (
    <div className={s.activityRow}>
      <ActivityFeed feed={metrics.feed} onNavigate={onNavigate} />
      <DriftTrendPanel metrics={metrics} />
    </div>
  );
}

function ActivityFeed({
  feed,
  onNavigate,
}: {
  feed: DashboardFeedItem[];
  onNavigate?: (target: DashboardDrillTarget) => void;
}) {
  const items = feed.slice(0, 6);
  return (
    <section className={s.panel} aria-labelledby="dash-activity-heading">
      <header className={s.panelHeader}>
        <Text
          as="h2"
          id="dash-activity-heading"
          size="sm"
          weight="semibold"
        >
          Recent activity
        </Text>
        {onNavigate ? (
          <button
            type="button"
            className={s.panelLink}
            onClick={() => onNavigate('findings')}
          >
            <Text as="span" size="xs" weight="medium">
              View findings
            </Text>
            <ArrowRight size={12} weight="bold" />
          </button>
        ) : null}
      </header>
      {items.length > 0 ? (
        <ul className={s.feedList}>
          {items.map((item, index) => (
            <li key={`${item.title}-${index}`} className={s.feedItem}>
              <span
                className={s.feedDot}
                data-status={item.status}
                aria-hidden="true"
              />
              <span className={s.feedBody}>
                <Text as="span" size="sm" className={s.feedTitle}>
                  {item.title}
                </Text>
                {item.meta ? (
                  <Text as="span" size="xs" color="tertiary">
                    {item.meta}
                  </Text>
                ) : null}
              </span>
              <Text
                as="span"
                size="xs"
                color="tertiary"
                tabularNums
                className={s.feedAgo}
              >
                {item.ago}
              </Text>
            </li>
          ))}
        </ul>
      ) : (
        <Text as="p" size="sm" color="tertiary" className={s.emptyFeed}>
          No activity yet. Governance events will appear here as CI runs.
        </Text>
      )}
    </section>
  );
}

function DriftTrendPanel({ metrics }: { metrics: DashboardMetrics }) {
  return (
    <section className={s.panel} aria-labelledby="dash-trend-heading">
      <header className={s.panelHeader}>
        <Text as="h2" id="dash-trend-heading" size="sm" weight="semibold">
          Drift over time
        </Text>
        <ChartLineUp
          size={14}
          className={s.panelHeaderIcon}
          aria-hidden="true"
        />
      </header>
      <Stack gap="md">
        <TrendRow
          label="Caught in CI"
          value={metrics.driftCaught.thisPeriod}
          data={metrics.driftTrend}
        />
        <TrendRow
          label="Shipped to main"
          value={metrics.driftShipped.thisPeriod}
          data={metrics.driftShipped.weeklyTrend}
        />
      </Stack>
    </section>
  );
}

function TrendRow({
  label,
  value,
  data,
}: {
  label: string;
  value: number;
  data: number[];
}) {
  return (
    <div className={s.trendRow}>
      <div className={s.trendMeta}>
        <Text as="span" size="xs" color="tertiary" className={s.trendLabel}>
          {label}
        </Text>
        <Text as="span" size="lg" weight="medium" tabularNums>
          {value}
        </Text>
      </div>
      <span className={s.trendSpark} aria-hidden="true">
        <SparkBg color="var(--fui-text-tertiary, #94a3b8)" data={data} />
      </span>
    </div>
  );
}
